import { Link } from "react-router-dom";
import PageBanner from "../../component/PageBanner";
import React from "react";
import { FaBolt, FaBullseye, FaEye } from "react-icons/fa";
import SocialMediaSidebar from "../SocialMediaSidebar";
import SectionHeaderNoPDF from "../SectionHeaderNoPDF";
import vision from "../../assets/images/vision.png";
import mission from "../../assets/images/mission.png";
import ScrollToTopButton from "../ScrollToTopButton";

const VisionMission = () => {
  return (
    <>
      <div>
        <SocialMediaSidebar />
      </div>
      <PageBanner
        title="Vision & Mission"
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "About Us" },
          { label: "Vision & Mission" },
        ]}
      />
      <div>
        <ScrollToTopButton />
      </div>

      <div className="max-w-6xl mx-auto p-4">
        <SectionHeaderNoPDF
          title="Vision & Mission @ GyanYantram"
          shareUrl="https://gyanyantram.in/vision-mission"
        />
      </div>

      {/* Main Section */}
      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-28 py-10 bg-white text-center font-poppins">
        <h3 className="text-xl sm:text-2xl font-semibold text-black mb-6">
          Empowering Urban Development through ICT Innovation
        </h3>

        {/* Icons + Video */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-10 mb-10">
          {/* Vision Icon */}
          <div className="w-28 h-28 sm:w-32 sm:h-32 md:w-40 md:h-40">
            <img
              src={vision}
              alt="Vision"
              className="w-full h-full object-contain"
            />
          </div>

          {/* Embedded YouTube Video */}
          <div className="aspect-video w-full max-w-md text-center">
            <iframe
              className="w-full h-full shadow-lg"
              src="https://www.youtube.com/embed/fdOfcUI_sWk?si=5S_MpFCK9d8Nxd6w"
              title="ICT Vision by GyanYantram"
              allowFullScreen
            ></iframe>
          </div>

          {/* Mission Icon */}
          <div className="w-28 h-28 sm:w-32 sm:h-32 md:w-40 md:h-40">
            <img
              src={mission}
              alt="Mission"
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        {/* Description */}
        <p className="text-black text-base sm:text-lg max-w-4xl mx-auto mb-10 px-2">
          At GyanYantram, we envision a future where urban infrastructure is intelligent, inclusive, and environmentally responsive. Our ICT-enabled solutions—ranging from public information systems to real-time sensors and WiFi—empower cities to become safer, smarter, and more sustainable.
        </p>

        {/* Vision & Mission Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto px-2">
          <div className="bg-white p-6 shadow-lg border rounded-lg">
            <div className="flex justify-center text-[#00D084] text-5xl sm:text-6xl mb-4">
              <FaEye />
            </div>
            <h4 className="text-lg sm:text-xl font-semibold mb-2">Vision</h4>
            <p className="text-black text-base sm:text-lg">
              To build an ecosystem of intelligent public infrastructure powered by ICT, enabling every citizen to benefit from connected, resilient, and transparent urban services.
            </p>
          </div>

          <div className="bg-white p-6 shadow-lg border rounded-lg">
            <div className="flex justify-center text-[#00D084] text-5xl sm:text-6xl mb-4">
              <FaBullseye />
            </div>
            <h4 className="text-lg sm:text-xl font-semibold mb-2">Mission</h4>
            <p className="text-black text-base sm:text-lg">
              To design and deploy ICT-driven solutions that modernize urban governance through real-time communication, safety systems, environmental monitoring, and digital access for all.
            </p>
          </div>
        </div>
      </div>

      {/* Extended Content */}
      <div className="bg-white w-full px-4 sm:px-6 md:px-10 lg:px-28 py-10 text-[#1a1a1a] font-sans text-justify">
        <h2 className="text-xl sm:text-2xl font-bold text-[#00D084] mb-4">
          The Future of Urban ICT Infrastructure
        </h2>

        <p className="text-black text-base sm:text-lg mb-6">
          Traditional infrastructure alone cannot support the fast-evolving demands of modern cities. GyanYantram's ICT framework integrates smart technologies—public communication tools, sensors, surveillance, and connectivity—into unified platforms to create livable, secure, and efficient urban environments.
        </p>

        {/* Core Components */}
        <h3 className="text-lg sm:text-xl font-bold mb-2">
          Core Components of GyanYantram’s ICT Solutions:
        </h3>
        <ul className="list-disc pl-6 text-black text-base sm:text-lg space-y-2">
          <li>
            <strong>Public Announcement Systems:</strong> Deliver urgent and informative messages to citizens citywide in real time.
          </li>
          <li>
            <strong>Variable Message Displays (VMDs):</strong> Share traffic updates, safety warnings, and community messages.
          </li>
          <li>
            <strong>High-Definition Surveillance:</strong> Strengthen public security and help manage law enforcement and civic operations.
          </li>
          <li>
            <strong>Emergency Communication Units:</strong> Connect people to emergency services quickly and efficiently.
          </li>
          <li>
            <strong>Free Public WiFi:</strong> Bridge the digital divide by offering secure internet access in urban spaces.
          </li>
          <li>
            <strong>Environmental & Weather Sensors:</strong> Monitor pollution levels, temperature, humidity, and toxic gases.
          </li>
          <li>
            <strong>Centralized IoT Dashboard:</strong> Real-time analytics and remote monitoring for proactive decision-making.
          </li>
        </ul>

        {/* Impact */}
        <h3 className="text-lg sm:text-xl font-bold mt-8 mb-2">Impact</h3>
        <p className="text-black text-base sm:text-lg mb-4">
          GyanYantram’s ICT ecosystem fosters safer streets, informed citizens, and streamlined urban operations. With real-time data and intelligent systems, municipal bodies can respond faster, allocate resources better, and enhance citizens’ day-to-day experience.
        </p>

        {/* Offerings */}
        <h3 className="text-lg sm:text-xl font-bold mt-8 mb-2">
          GyanYantram’s Urban ICT Offerings
        </h3>
        <p className="text-black text-base sm:text-lg mb-2">
          Our scalable and customizable solutions include:
        </p>
        <ul className="list-disc pl-6 text-black text-base sm:text-lg space-y-2 mb-4">
          <li>
            <strong>Custom ICT Infrastructure:</strong> Adaptable systems including sensors, displays, emergency units, and surveillance for varied city needs.
          </li>
          <li>
            <strong>AI-Enabled IoT Platform:</strong> Smart dashboards for visualization, anomaly detection, and predictive insights.
          </li>
          <li>
            <strong>Interoperability with Smart Cities:</strong> Fully integrable with transport, utilities, and public safety systems.
          </li>
        </ul>
        <p className="text-black text-base sm:text-lg">
          Learn how we’re transforming cities with intelligent systems at{" "}
          <a
            href="https://gyanyantram.in"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#00D084] hover:text-[#00b075] transition"
          >
            gyanyantram.in
          </a>
        </p>
      </div>
    </>
  );
};

export default VisionMission;
